import MuiTooltip from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

import editIcon from 'images/directory/edit_icon.svg';
import locationIcon from 'images/directory/location_icon_grey.svg';
import messageIcon from 'images/directory/message_icon.svg';
import instagramLogo from 'images/instagram_black.svg';
import linkLogo from 'images/link_black.svg';
import linkedinLogo from 'images/linkedin_black.svg';
import twitterLogo from 'images/twitter_black.svg';

import { useAppContext } from '../../contexts/AppContext';
import { useSubscriberProfile, useUserForumPosts } from '../../hooks';
import { edit_profile_settings_path, subscriber_directories_path } from '../../routes';
import { FollowType } from '../../utils/backend-api/follow';
import {
  SubscriberCommentsParams,
  SubscriberCommentsResponse,
  fetchMoreComments,
} from '../../utils/backend-api/subscriber_directory';
import { Spinner, Tooltip } from '../BaseUI';
import { EnableProfileModal, FollowButton, FollowStatusBanner, MessageModal } from '../shared';
import MetaTags from '../shared/MetaTags';
import Layout from './Layout';

const cookies = new Cookies();

const OG_IMAGE_PATH = '/global/og_images/profile_social_cover.png';

const ProfileDetail = () => {
  const { isWebview } = useAppContext();
  const { username, article_page_id } = useParams();
  const [articlePageId, setArticlePageId] = useState(article_page_id || 1);
  const [messageUser, setMessageUser] = useState(null);
  const [subscriberComments, setSubscriberComments] = useState([]);
  const [subscriberCommentLikes, setSubscriberCommentLikes] = useState([]);
  const [commentsNextPage, setCommentsNextPage] = useState(1);
  const [commentLikesNextPage, setCommentLikesNextPage] = useState(1);
  const [showTooltip, setShowTooltip] = useState(true);
  const { profile, isLoadingSubscriberProfile } = useSubscriberProfile(username, articlePageId);
  const canViewFullProfile = profile?.isCurrentUserSubscriber || profile?.isReporter;
  // If canViewFullProfile is false use id 0 to get an empty forumPosts array to hide it
  const { forumPosts } = useUserForumPosts({
    userId: canViewFullProfile ? profile?.id : 0,
    order: 'most_recent',
  });
  const queryClient = useQueryClient();

  // For Directory/Forum Launch - TODO: remove after launch
  useEffect(() => {
    const hasVisitedProfilePage = cookies.get('hasVisitedProfilePage');
    cookies.set(
      'hasVisitedProfilePage',
      (hasVisitedProfilePage ? parseInt(hasVisitedProfilePage) || 0 : 0) + 1
    );
  }, []);

  useEffect(() => {
    // TODO: we could derive this data from the profile instead of using setState
    if (!isLoadingSubscriberProfile) {
      setSubscriberComments(profile.comments || []);
      setCommentsNextPage(profile.subscriberCommentsNextPage);
      setSubscriberCommentLikes(profile.commentLikes || []);
      setCommentLikesNextPage(profile.commentLikesNextPage);
    }
  }, [
    isLoadingSubscriberProfile,
    profile?.comments,
    profile?.subscriberCommentsNextPage,
    profile?.commentLikes,
    profile?.commentLikesNextPage,
  ]);

  const { mutate: fetchComments, isLoading: isfetchMoreComment } = useMutation(
    async (sanitizedParams: SubscriberCommentsParams) => await fetchMoreComments(sanitizedParams),
    {
      onSuccess: (data: SubscriberCommentsResponse) => {
        setSubscriberComments(subscriberComments.concat(data.comments));
        setCommentsNextPage(data.nextPage);
      },
    }
  );

  const { mutate: fetchCommentLikes, isLoading: isfetchMoreCommentLikes } = useMutation(
    async (sanitizedParams: SubscriberCommentsParams) => await fetchMoreComments(sanitizedParams),
    {
      onSuccess: (data: SubscriberCommentsResponse) => {
        setSubscriberCommentLikes(subscriberCommentLikes.concat(data.comments));
        setCommentLikesNextPage(data.nextPage);
      },
    }
  );

  const fetchHandler = (commentType: 'Comment' | 'CommentLike') => {
    let sanitizedParams: SubscriberCommentsParams = {
      user_id: profile.id,
      comment_type: 'comments',
      page: 1,
    };
    switch (commentType) {
      case 'Comment':
        sanitizedParams = {
          user_id: profile.id,
          comment_type: 'comments',
          page: commentsNextPage,
        };
        break;
      case 'CommentLike':
        sanitizedParams = {
          user_id: profile.id,
          comment_type: 'comment_likes',
          page: commentLikesNextPage,
        };
        break;
      default:
        console.log('Unknown type');
        return;
    }
    commentType == 'Comment' ? fetchComments(sanitizedParams) : fetchCommentLikes(sanitizedParams);
  };

  const isMessageModalEnabled = profile?.canSendMessage || profile?.requiresMessageEnable;
  const hasFollowRelationships = profile?.totalFollowers > 0 || profile?.totalFollowing > 0;

  const metaDescription = (profile) => {
    return [profile.titleAtCompany, profile.companyName, profile.oneLineBio]
      .filter((str) => str !== null && str !== '' && str !== undefined)
      .join(' | ');
  };

  return (
    <React.Fragment>
      {isLoadingSubscriberProfile ? (
        <Spinner />
      ) : (
        <>
          <MetaTags
            title={`${profile.name} - The Information`}
            description={metaDescription(profile)}
            ogTitle={profile.name}
            ogImagePath={OG_IMAGE_PATH}
            twitterTitle={profile.name}
            ogSiteName="The Information"
          />
          <Container id="Subscriber">
            {profile.showEnableProfileModal && (
              <EnableProfileModal
                isLoggedIn={profile?.currentUserLoggedIn}
                isSubscriber={profile?.isCurrentUserSubscriber}
              />
            )}
            <Row className="profile-start">
              <Col className="col-sm-12 col-md-8">
                <div className="profile-image-wrapper">
                  <div className="profile-img">
                    {profile.picture ? (
                      <img src={profile.picture} />
                    ) : (
                      <div className="avatar-placeholder">
                        <div className="avatar-name">{profile.nameInitial}</div>
                      </div>
                    )}
                  </div>
                  <div className="profile-header-detail">
                    <div className="profile-header">
                      <h1 className="name-container">{profile.name}</h1>
                      {/* don't render the button until you know the followStatus */}
                      {profile.followStatus !== null &&
                        profile.currentUserId !== profile.id &&
                        profile.isActive &&
                        profile.isCurrentUserSubscriber && (
                          <FollowButton
                            tooltipEnabled
                            followStatus={profile.followStatus}
                            followeeId={profile.identityId}
                            followType={FollowType.Identity}
                            username={username}
                            queryClient={queryClient}
                            name={profile.name}
                          />
                        )}
                    </div>
                    <div className="profile-header-wrapper">
                      {profile.oneLineBio && (
                        <div className="one-line-bio-wrapper">
                          <div className="one-line-bio-container">{profile.oneLineBio}</div>
                        </div>
                      )}
                      {(profile.titleAtCompany || profile.companyName) && (
                        <div className="profile-subheader">
                          <div>{profile.titleAtCompany}</div>
                          {profile.titleAtCompany && profile.companyName && (
                            <div className="dot-middle">.</div>
                          )}
                          {profile.companyName && (
                            <a href={subscriber_directories_path({ company: profile.companyName })}>
                              {profile.companyName}
                            </a>
                          )}
                        </div>
                      )}
                      {profile.isActive && (
                        <div className="profile-subheader">
                          Subscriber Since {profile.subscriberSince}
                        </div>
                      )}
                      {canViewFullProfile ? (
                        <div className="social-contact-container">
                          <div className="social-contact-wrapper">
                            {profile.twitter && (
                              <div className="social-container">
                                <a
                                  href={`https://www.twitter.com/${profile.twitter}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img src={twitterLogo} />
                                </a>
                              </div>
                            )}
                            {profile.linkedin && (
                              <div className="social-container">
                                <a
                                  href={`https://www.linkedin.com/in/${profile.linkedin}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img src={linkedinLogo} />
                                </a>
                              </div>
                            )}
                            {profile.instagram && (
                              <div className="social-container">
                                <a
                                  href={`https://www.instagram.com/${profile.instagram}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img src={instagramLogo} />
                                </a>
                              </div>
                            )}
                            {profile.personalUrl && (
                              <div className="social-container">
                                <a href={profile.personalUrl} target="_blank" rel="noreferrer">
                                  <img src={linkLogo} />
                                </a>
                              </div>
                            )}
                          </div>
                          {profile.location && (
                            <a
                              className="location-wrapper"
                              href={subscriber_directories_path({ location: profile.location })}
                            >
                              <img src={locationIcon}></img>
                              <div>{profile.location}</div>
                            </a>
                          )}
                        </div>
                      ) : (
                        // Show social media icons without links for leads and logged out users
                        <div className="social-contact-container">
                          <div className="social-contact-wrapper">
                            <div className="social-container">
                              <img src={twitterLogo} />
                            </div>
                            <div className="social-container">
                              <img src={linkedinLogo} />
                            </div>
                            <div className="social-container">
                              <img src={instagramLogo} />
                            </div>
                            <div className="social-container">
                              <img src={linkLogo} />
                            </div>
                            <div className="social-container">
                              <img src={locationIcon} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
              {profile.isActive && profile.isCurrentUserSubscriber && (
                <Col className="col-sm-12 col-md-4 contact-wrapper">
                  <div className="contact-container">
                    <div
                      className={`profile-contact-wrapper ${
                        hasFollowRelationships ? 'with-follow-banner' : ''
                      }`}
                    >
                      {hasFollowRelationships && (
                        <FollowStatusBanner
                          username={username}
                          followers={profile.followers}
                          following={profile.following}
                          totalFollowers={profile.totalFollowers}
                          totalFollowing={profile.totalFollowing}
                          queryClient={queryClient}
                        />
                      )}
                      {profile.isOwnProfile ? (
                        <Button
                          className="profile-button"
                          onClick={() => (window.location = edit_profile_settings_path())}
                        >
                          <img src={editIcon} />
                          Update My Profile
                        </Button>
                      ) : (
                        <>
                          <div className="message-btn-container">
                            {profile?.currentUserCanMessage && !profile?.subHasMessageEnabled ? (
                              <MuiTooltip
                                title="This person has messaging disabled"
                                enterTouchDelay={0}
                                arrow
                              >
                                <div>
                                  <Button
                                    className="profile-button"
                                    disabled={!isMessageModalEnabled}
                                    onClick={() => setMessageUser(profile)}
                                    data-test="message-button"
                                  >
                                    <img src={messageIcon} />
                                    <span>Send A Message</span>
                                  </Button>
                                </div>
                              </MuiTooltip>
                            ) : (
                              <Button
                                className="profile-button"
                                disabled={!isMessageModalEnabled}
                                onClick={() => setMessageUser(profile)}
                                data-test="message-button"
                              >
                                <img src={messageIcon} />
                                <span>Send A Message</span>
                              </Button>
                            )}
                            {/* // For Directory/Forum Launch - TODO: remove after launch */}
                            {!isWebview &&
                              (!cookies.get('hasVisitedProfilePage') ||
                                cookies.get('hasVisitedProfilePage') == 1) && (
                                <Tooltip
                                  actionMessage="Got It!"
                                  message="You can now message subscribers"
                                  isLaunchTooltip
                                  onClose={() => setShowTooltip(false)}
                                  showTooltip={showTooltip}
                                />
                              )}
                          </div>
                          {isMessageModalEnabled && messageUser && (
                            <MessageModal user={messageUser} onClose={() => setMessageUser(null)} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            <Layout
              username={username}
              setArticlePageId={setArticlePageId}
              bio={profile.bio}
              books={profile.favoriteBooks}
              comments={subscriberComments}
              commentLikes={subscriberCommentLikes}
              commentLikesNextPage={commentLikesNextPage}
              fetchHandler={fetchHandler}
              findingNewJob={profile.findingNewJob}
              findingNewJobDesc={profile.findingNewJobDesc}
              forumPosts={forumPosts}
              hiring={profile.hiring}
              hiringDesc={profile.hiringDesc}
              isfetchMoreComment={isfetchMoreComment}
              isfetchMoreCommentLikes={isfetchMoreCommentLikes}
              investingInStartups={profile.investingInStartups}
              investingInStartupsDesc={profile.investingInStartupsDesc}
              name={profile.name}
              picture={profile.picture}
              providingServices={profile.providingServices}
              providingServicesDesc={profile.providingServicesDesc}
              shows={profile.favoriteShows}
              skills={profile.skills}
              sportsToPlay={profile.favoriteSportsToPlay}
              sportsToWatch={profile.favoriteSportsToWatch}
              subscriberCommentsNextPage={commentsNextPage}
              topics={profile.topics}
              vacationSpots={profile.favoriteVacationSpots}
              contributorContent={profile.contributorContent}
              contributorContentPagination={profile.contributorContentPagination}
              isCurrentUserLead={profile.isCurrentUserLead}
              isOwnProfile={profile.isOwnProfile}
              canViewFullProfile={canViewFullProfile}
            />
          </Container>
        </>
      )}
    </React.Fragment>
  );
};

export default ProfileDetail;
